import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import { ChartOrderItem, DataType } from '../../utils/types/analytics/analytics_types';
import { DashletIconGroup } from './DashletIconGroup';

type TableDashletProps = {
  id: string;
  data: Array<DataType>;
  title: string;
  dashletItem: ChartOrderItem;
  onDragStart: (item: ChartOrderItem) => void;
  onDragEnter: (item: ChartOrderItem) => void;
  onDragOver: (event: React.DragEvent<HTMLDivElement>) => void;
  onDrop: (event: React.DragEvent<HTMLDivElement>) => void;
  fullScreenDetected: boolean;
  setFullScreenDetected: (fullscreen: boolean) => void;
  handleDashletVisibility: (item: ChartOrderItem) => void;
  editMode: boolean;
};

export const TableDashlet = ({
  id,
  data,
  title,
  dashletItem,
  onDragStart,
  onDragEnter,
  onDragOver,
  onDrop,
  setFullScreenDetected,
  handleDashletVisibility,
  editMode,
}: TableDashletProps) => {
  const handleDragStart = () => {
    if (onDragStart) {
      onDragStart(dashletItem);
    }
  };

  const handleDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (onDragEnter) {
      onDragEnter(dashletItem);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (onDragOver) {
      onDragOver(event);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (onDrop) {
      onDrop(event);
    }
  };

  return (
    <Paper
      variant="outlined"
      className="group"
      sx={{
        position: 'relative',
        width: '100%',
        gridColumn: 'span 2',
        borderRadius: '16px',
        p: 2.5,
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.06)',
          borderColor: 'primary.main',
        },
      }}
    >
      <DashletIconGroup
        id={id}
        dashletItem={dashletItem}
        setFullScreenDetected={setFullScreenDetected}
        handleDashletVisibility={handleDashletVisibility}
        editMode={editMode}
      />

      <Box
        id={id}
        draggable
        onDragStart={handleDragStart}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        sx={{
          opacity: dashletItem.hidden ? 0.4 : 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: '0.875rem',
            color: 'text.primary',
            mb: 2,
            textAlign: 'center',
          }}
        >
          {title}
        </Typography>

        {!data || data.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: 260,
              color: 'text.secondary',
              fontSize: '0.875rem',
            }}
          >
            No data to preview
          </Box>
        ) : (
          <TableContainer
            sx={{
              width: { xs: '100%', md: '85%' },
              borderRadius: '6px',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Table size="small">
              <TableBody>
                {data.map((item, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      bgcolor: index % 2 === 0 ? 'rgba(37, 99, 235, 0.04)' : 'background.paper',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <TableCell
                      sx={{
                        fontSize: '0.8125rem',
                        fontWeight: 500,
                        color: 'text.primary',
                        borderBottom: index < data.length - 1 ? '1px solid' : 'none',
                        borderColor: 'divider',
                        py: 1,
                        px: 2,
                      }}
                    >
                      {item.name}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontSize: '0.8125rem',
                        fontWeight: 600,
                        color: 'primary.main',
                        borderBottom: index < data.length - 1 ? '1px solid' : 'none',
                        borderColor: 'divider',
                        py: 1,
                        px: 2,
                      }}
                    >
                      {item.value}%
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Paper>
  );
};
